import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { handleApiCall } from "../../../apis";
import { endpoints } from "../../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../../utils/constants";
import viewImage from "../../../assets/images/icons/view.svg";
import Star from "../../../assets/images/icons/star.svg";
import SimplePagePagination from "../../../commons/Pagination/SimplePagePagination";
import { applyListState } from "../../../utils/listNavigation";

const RestaurantList = ({ listState: listStateProp } = {}) => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [featuredAction, setFeaturedAction] = useState("");
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const restoredRef = useRef(false);

  const getRestaurants = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        approval_status: "approved",
        // approval_status: 1, // Only show approved restaurants
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await handleApiCall("get", `${endpoints.restaurantList}?${params}`);
      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const data = response?.data?.data?.data || [];
        // Client-side filter to ensure only approved restaurants (approval_status === 1)
        // const approvedRestaurants = data.filter((restaurant) => restaurant.approval_status === 1);
        // setRestaurants(approvedRestaurants);
        setRestaurants(data)
        setTotalPages(response?.data?.data?.pagination?.totalPages || 1);
      }
      // API errors are already handled by handleApiCall
    } catch (error) {
      console.error("Error fetching restaurants:", error);
      // Network errors are already handled by handleApiCall
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    getRestaurants();
  }, [getRestaurants]);

  useEffect(() => {
    const listState = listStateProp || location.state?.listState;
    if (!listState || restoredRef.current) return;
    if (listState.tabType && listState.tabType !== "restaurant_list") return;
    restoredRef.current = true;
    applyListState(listState, { setCurrentPage, setSearchTerm });
  }, [listStateProp, location.state]);

  useEffect(() => {
    // Update selectAll state based on individual selections
    if (restaurants.length > 0) {
      setSelectAll(selectedRestaurants.length === restaurants.length);
    } else {
      setSelectAll(false);
    }
  }, [selectedRestaurants, restaurants.length]);

  const listNavState = {
    tabType: "restaurant_list",
    currentPage,
    searchTerm,
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedRestaurants(restaurants.map((restaurant) => restaurant._id));
    } else {
      setSelectedRestaurants([]);
    }
  };

  const handleSelectRestaurant = (restaurantId, checked) => {
    if (checked) {
      setSelectedRestaurants([...selectedRestaurants, restaurantId]);
    } else {
      setSelectedRestaurants(selectedRestaurants.filter((id) => id !== restaurantId));
    }
  };

  const handleRestaurantStatusToggle = async (restaurantId, currentStatus) => {
    if (!restaurantId) {
      toast.error("Restaurant ID not found");
      return;
    }

    const newStatus = Number(currentStatus) === 1 ? 0 : 1;
    setStatusUpdatingId(restaurantId);

    try {
      const response = await handleApiCall("patch", `${endpoints.restaurantStatus}/${restaurantId}`, {
        status: newStatus,
      });

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(
          response?.data?.message ||
            `Restaurant ${newStatus === 1 ? "activated" : "deactivated"} successfully`
        );
        getRestaurants();
      }
    } catch (error) {
      console.error("Error updating restaurant status:", error);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleFeaturedAction = () => {
    if (!featuredAction) {
      toast.error("Please select an action");
      return;
    }
    if (selectedRestaurants.length === 0) {
      toast.error("Please select at least one restaurant");
      return;
    }

    // Map to restaurant_id._id for API payload
    const restaurantIds = restaurants
      .filter((r) => selectedRestaurants.includes(r._id))
      .map((r) => r?.restaurant_id?._id)
      .filter(Boolean);

    if (restaurantIds.length === 0) {
      toast.error("Selected restaurants don't have valid IDs");
      return;
    }

    const isFeatured = featuredAction === "Mark as Featured";

    const payload = {
      restaurant_ids: restaurantIds,
      is_featured: isFeatured,
    };

    handleApiCall("post", endpoints.addFeaturedRestaurant, payload)
      .then((response) => {
        if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
          toast.success(`${featuredAction} action applied to ${restaurantIds.length} restaurant(s)`);
          setSelectedRestaurants([]);
          setSelectAll(false);
          setFeaturedAction("");
          getRestaurants();
        } else {
          toast.error(response?.data?.message || "Failed to update featured status");
        }
      })
      .catch((error) => {
        console.error("Error updating featured status:", error);
      });
  };

  return (
    <>
      {/* Search Bar */}
      <div className="row mb-4 mx-0 px-0">
        <div className="col-md-6 px-0">
          <div className="form-group my-0">
            <input type="text" className="form-control search-icon left-icon border" placeholder={UI_CONSTANTS.PLACEHOLDERS.RESTAURANT_SEARCH} value={searchTerm} onChange={handleSearch} />
          </div>
        </div>
        <div className="col-md-6 d-flex justify-content-end align-items-center px-0">
          {/* {selectedRestaurants.length > 0 && <span className="mr-3 text-muted">{selectedRestaurants.length} restaurant(s) selected</span>} */}
          <div className="d-flex">
            <select className="form-control mr-2" style={{ width: "200px" }} value={featuredAction} onChange={(e) => setFeaturedAction(e.target.value)}>
              <option value="">Select Action</option>
              <option value="Mark as Featured">Mark as Featured</option>
              <option value="Unmark as Featured">Unmark as Featured</option>
            </select>
            <button className="btn btn-primary" onClick={handleFeaturedAction} disabled={!featuredAction || selectedRestaurants.length === 0}>
              Save
            </button>
          </div>
        </div>
      </div>

      <div className="table-responsive px-0">
        <table className="table table-striped trim">
          <thead>
            <tr>
              <th width="50">
                <input type="checkbox" checked={selectAll} onChange={(e) => handleSelectAll(e.target.checked)} style={{ margin: "0px" }} />
              </th>
              <th width="80">Restaurant ID</th>
              <th width="150">Restaurant Name</th>
              <th>Owner/Manager Name</th>
              <th>Email id</th>
              {/* <th>Mobile Number</th> */}
              <th>
                Menu
                <br />
                Catalogue
              </th>
              <th className="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : restaurants.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-4">
                  {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                </td>
              </tr>
            ) : (
              restaurants.map((restaurant, index) => (
                <tr key={restaurant._id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <input type="checkbox" checked={selectedRestaurants.includes(restaurant._id)} onChange={(e) => handleSelectRestaurant(restaurant._id, e.target.checked)} />
                      {(() => {
                        const isFeatured = restaurant?.restaurant_id?.is_restaurant_featured === true;
                        return (
                          <img
                            src={Star}
                            alt={isFeatured ? "Featured" : "Not Featured"}
                            style={{
                              width: 16,
                              height: 16,
                              marginLeft: 8,
                              opacity: isFeatured ? 1 : 0.35,
                            }}
                          />
                        );
                      })()}
                    </div>
                  </td>
                  <td>#{restaurant._id.slice(-8)}</td>
                  <td>{restaurant.restaurant_id?.restaurant_name || "N/A"}</td>
                  <td>{restaurant.restaurant_id?.full_name || "N/A"}</td>
                  <td>{restaurant.restaurant_id?.email || "N/A"}</td>
                  {/* <td>{restaurant.restaurant_id?.phone ? `+${restaurant.restaurant_id.phone}` : "N/A"}</td> */}
                  <td>
                    <Link
                      to={`/menu-and-restaurant/view-menu-catalogue/${restaurant?.restaurant_id?._id}/${encodeURIComponent(
                        restaurant.restaurant_id?.restaurant_name || "Restaurant"
                      )}`}
                      className="text-black"
                    >
                      <u>View</u>
                    </Link>
                  </td>
                  <td>
                    <div className="d-flex align-items-center justify-content-center">
                      <Link
                        to={`/menu-and-restaurant/view-restaurant/${restaurant._id}`}
                        state={{
                          from: "/menu-and-restaurant-management",
                          listState: listNavState,
                        }}
                        className="mx-2"
                      >
                        <img src={viewImage} alt="View" />
                      </Link>
                      <label className="custom-control custom-switch p-1 ml-4 mb-0 mt-1" role="button">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          checked={Number(restaurant.status) === 1}
                          disabled={statusUpdatingId === restaurant.restaurant_id?._id}
                          onChange={() =>
                            handleRestaurantStatusToggle(restaurant.restaurant_id?._id, restaurant.status)
                          }
                        />
                        <span className="custom-control-label custom-toggle" />
                      </label>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <SimplePagePagination page={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
    </>
  );
};

export default RestaurantList;
